import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import { BACKEND_URL } from '../config';
import {
  RequestInput,
  UserJourney,
  UserGetIn,
  UserGetOut,
  UserNutrientSummary,
  ProductOut,
  Subscription,
} from '../types';
import { CmsClient } from '../cms/sanity';
import { Product } from '../cms/types';
import userServices from '../utils/user';
import { userData, TestUsers } from './userpersonas';
import useAPI from './useApi';
import { APIRequests } from '../config/api';
import { isProd } from '.';

export interface GlobalStateInterface {
  mode: string;
  user: UserGetOut;
  userNutrients: UserNutrientSummary[];
  journey: UserJourney[];
  products: Map<string, Product>;
  subscriptions: Subscription[];
  cart: Product[];
}

export type GlobalContext = {
  state: GlobalStateInterface;
  setState: Dispatch<SetStateAction<GlobalStateInterface>>;
  addToCart: (product: Product) => void;
  removeFromCart: (product: Product) => void;
};

const initialState: GlobalStateInterface = {
  mode: process.env.REACT_APP_MODE || 'prod',
  user: {} as UserGetOut,
  userNutrients: {} as UserNutrientSummary[],
  journey: {} as UserJourney[],
  products: {} as Map<string, Product>,
  subscriptions: {} as Subscription[],
  cart: [],
};

const GlobalStateContext = createContext({
  state: { ...initialState },
  setState: {} as Dispatch<SetStateAction<GlobalStateInterface>>,
  addToCart: (product: Product) => {},
  removeFromCart: (product: Product) => {},
});

const cartReducer = (cart: Product[], action: any) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      if (!cart.some((item) => item.code === action.product.code)) {
        return [...cart, action.product];
      }
      return cart;

    case 'REMOVE_FROM_CART':
      return cart.filter((item) => item.code !== action.product.code);

    default:
      return cart;
  }
};

const GlobalStateProvider = ({
  children,
  value = { ...initialState } as GlobalStateInterface,
}: {
  children: React.ReactNode;
  value?: GlobalStateInterface;
}): JSX.Element => {
  const [state, setState] = useState(value);
  const [cart, cartDispatch] = useReducer(cartReducer, initialState.cart);

  const addToCart = (product: Product) => {
    cartDispatch({ type: 'ADD_TO_CART', product });
  };

  const removeFromCart = (product: Product) => {
    cartDispatch({ type: 'REMOVE_FROM_CART', product });
  };
  return (
    <GlobalStateContext.Provider
      value={{ state: { ...state, cart }, setState, addToCart, removeFromCart }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

const getProducts = async () => {
  const sanityProducts = await CmsClient.fetchAllProducts();
  const recurlyProducts: ProductOut[] = await fetch(
    `${BACKEND_URL}/products/recurly`
  ).then((response) => {
    return response.json();
  });
  let productMap = new Map<string, Product>();
  recurlyProducts.forEach((rProduct: ProductOut) => {
    let sProduct = sanityProducts.filter((prod: Product) => {
      return prod.code === rProduct.code;
    });
    if (sProduct.length) {
      sProduct[0].price = rProduct.price;
      sProduct[0].intervalLength = rProduct.interval_length;
      sProduct[0].intervalUnit = rProduct.interval_unit;
      sProduct[0].totalBillingCycles = rProduct.total_billing_cycles;
      productMap.set(sProduct[0].code, sProduct[0]);
    }
  });
  const bundles = sanityProducts.filter((prod) => {
    return prod.bundleItems && prod.bundleItems.length;
  });
  bundles.forEach((bundle) => {
    bundle.price = 0;
    for (const bundleItem of bundle.bundleItems!) {
      const bundleProduct = productMap.get(bundleItem.code);
      if (!bundleProduct) continue;
      let productPrice = bundleProduct.price;
      if (productPrice && bundleItem.discountPercentage) {
        productPrice =
          productPrice * ((100 - bundleItem.discountPercentage) / 100);
      }
      if (productPrice && bundle.bundleDiscountPercentage) {
        productPrice =
          productPrice * ((100 - bundle.bundleDiscountPercentage) / 100);
      }
      bundle.price += productPrice!;
    }
    productMap.set(bundle.code, bundle);
  });
  return productMap;
};

const useGlobalState = (initialize?: Boolean): GlobalContext => {
  const context = useContext(GlobalStateContext);
  if (!context) {
    throw new Error('useGlobalState must be used within a GlobalStateContext');
  }

  const user = useAPI<UserGetOut>(APIRequests.UserMe);
  const nutrientData = useAPI<UserNutrientSummary[]>(
    APIRequests.UserNutrientsList
  );
  const subscriptionData = useAPI<Subscription[]>(
    APIRequests.UserSubscriptionsList
  );
  const userEmail = userServices.getEmail();
  const userToken = userServices.getToken();
  const rdata: UserGetIn = {
    email: userEmail,
    token: userToken,
  };

  const req: RequestInput = { queryParams: rdata };
  useEffect(() => {
    if (!context.state.user === false && initialize) {
      user.execute(req);
      nutrientData.execute(req);
      subscriptionData.execute({});
      if (!context.state.products.size) {
        getProducts().then((data) => {
          context.setState((prevState) => ({
            ...prevState,
            ...{ products: data },
          }));
        });
      }
    }
  }, []);

  useEffect(() => {
    if (
      initialize &&
      nutrientData.data &&
      context.state.userNutrients !== nutrientData.data
    ) {
      context.setState((prevState) => ({
        ...prevState,
        ...{ userNutrients: nutrientData.data },
      }));
    }
  }, [nutrientData.data]);

  useEffect(() => {
    if (initialize) {
      context.setState((prevState) => ({
        ...prevState,
        ...{
          subscriptions: subscriptionData.data,
        },
      }));
    }
  }, [subscriptionData.data]);

  useEffect(() => {
    if (initialize) {
      if (context.state.mode === 'develop') {
        context.setState((prevState) => ({
          ...prevState,
          ...{ user: userData[TestUsers.NewUser] },
        }));
        // @ts-ignore: Property does not exist
        analytics.identify(userData[TestUsers.NewUser]?.id, {
          name: userData[TestUsers.NewUser]?.name,
          firstName: userData[TestUsers.NewUser]?.first_name,
          lastName: userData[TestUsers.NewUser]?.last_name,
          email: userData[TestUsers.NewUser]?.email,
        });
      } else if (user.data) {
        context.setState((prevState) => ({
          ...prevState,
          ...{ user: user.data },
        }));
        if (isProd) {
          try {
            // @ts-ignore: Property does not exist
            analytics.identify(user.data.id, {
              name: user.data.name,
              firstName: user.data.first_name,
              lastName: user.data.last_name,
              email: user.data.email,
            });
            if (window) {
              // @ts-ignore: Property does not exist
              window.Atlas.identify({
                userId: user.data.id,
                name: user.data.name,
                email: user.data.email,
                phone: user.data.phone,
              });
              // @ts-ignore: any type
              window['friendbuy'].push([
                'track',
                'customer',
                {
                  id: user.data.id,
                  email: user.data.email,
                  first_name: user.data.first_name,
                  last_name: user.data.last_name,
                },
              ]);
            }
          } catch (err) {
            console.error(err);
          }
        }
      }
    }
  }, [user.data]);

  return context;
};

export { GlobalStateProvider, useGlobalState };
